import type { TextInputProps } from '@meterup/atto';
import {
  CompositeField,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  FieldContainer,
  FloatingField,
  FloatingImmediateErrorField,
  Icon,
  MultiComboBox,
  MultiComboBoxItem,
  Select,
  SelectItem,
  styled,
  TextInput,
} from '@meterup/atto';
import { isNumber } from 'lodash-es';
import {
  type Key,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDebouncedCallback } from 'use-debounce';

import type { FilterOption } from '../../createColumnBuilder';
import {
  DateTimeRangePickerDropdownContents,
  useDateTimeRangePicker,
} from '../../../DateTimeRangePicker/DateTimeRangePicker';
import { TimePeriod } from '../../../DateTimeRangePicker/utils';

export const TableFiltersWiderContainer = styled('div', {
  width: '$320',
});
TableFiltersWiderContainer.displayName = 'TableFiltersWiderContainer';

// Context for managing nested dropdown state
const NestedDropdownContext = createContext<{
  isNestedDropdownActive: boolean;
  setNestedDropdownActive: (active: boolean) => void;
}>({
  isNestedDropdownActive: false,
  setNestedDropdownActive: () => {},
});

export const useTableFiltersFieldNestedDropdown = () => useContext(NestedDropdownContext);

export function TableFiltersFieldNestedDropdownProvider({ children }: { children: ReactNode }) {
  const [isNestedDropdownActive, setNestedDropdownActive] = useState(false);

  const contextValue = useMemo(
    () => ({ isNestedDropdownActive, setNestedDropdownActive }),
    [isNestedDropdownActive, setNestedDropdownActive],
  );

  return (
    <NestedDropdownContext.Provider value={contextValue}>{children}</NestedDropdownContext.Provider>
  );
}

type CommonFilterProps = {
  id: string;
  label: string;
};

type TextFilterProps = CommonFilterProps & {
  value: string;
  onChange: (id: string, value: string) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

type SelectFilterProps = CommonFilterProps & {
  value: string;
  options: FilterOption[];
  handleFiltersChange: (id: string, key: string) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onValueSelected?: () => void;
};

type MultiSelectFilterProps = CommonFilterProps & {
  value: string[];
  options: FilterOption[];
  handleFiltersChange: (id: string, values: string[]) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onValueSelected?: () => void;
};

const TableFiltersFieldRow = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$4',
  padding: '$6 $8',
  width: '100%',
});
TableFiltersFieldRow.displayName = 'TableFiltersFieldRow';

export function TableFiltersFieldSelectFilter({
  id,
  label,
  value,
  handleFiltersChange,
  options,
  onKeyDown,
  onValueSelected,
}: SelectFilterProps) {
  if (options) {
    return (
      <FieldContainer background="application">
        <CompositeField
          label={label}
          element={
            <TableFiltersFieldRow>
              <Select
                popoverProps={{ className: `disableOutsideClick_${id}` }}
                aria-label={label}
                value={value}
                onValueChange={(key: string) => {
                  handleFiltersChange(id, key);
                  onValueSelected?.();
                }}
                size="medium"
                width="100%"
                onKeyDown={onKeyDown}
              >
                {options.map((option) => (
                  <SelectItem
                    key={option.value}
                    textValue={typeof option.label === 'string' ? option.label : option.value}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </Select>
            </TableFiltersFieldRow>
          }
        />
      </FieldContainer>
    );
  }
}

export function TableFiltersFieldTableFiltersFieldMultiSelectFilter({
  id,
  label,
  value,
  handleFiltersChange,
  options,
  onKeyDown,
  onValueSelected,
}: MultiSelectFilterProps) {
  if (options) {
    return (
      <FloatingField
        label={label}
        element={
          <TableFiltersFieldRow>
            <MultiComboBox
              className={`disableOutsideClick_${id}`}
              onKeyDown={onKeyDown}
              aria-label={label}
              value={value}
              onValueChange={(keys: 'all' | Set<Key>) => {
                handleFiltersChange(
                  id,
                  keys === 'all' ? [] : Array.from(keys).map((k) => k.toString()),
                );
                onValueSelected?.();
              }}
              width="100%"
              enableSelectAll
            >
              {options.map((option) => (
                <MultiComboBoxItem
                  key={option.value}
                  textValue={typeof option.label === 'string' ? option.label : option.value}
                >
                  {option.label}
                </MultiComboBoxItem>
              ))}
            </MultiComboBox>
          </TableFiltersFieldRow>
        }
      />
    );
  }
}

export function TableFiltersFieldDebouncedInput({
  id,
  value,
  label,
  onChange,
  onKeyDown,
  parseValue,
  onValueSelected,
  disableAutoFocus,
  ...restProps
}: Omit<TextInputProps, 'onChange'> & {
  id: string;
  onChange: (id: string, value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  parseValue?: (value: string) => string;
  onValueSelected?: () => void;
  disableAutoFocus?: boolean;
}) {
  const [localVal, setLocalVal] = useState(value ?? '');
  const inputRef = useRef<HTMLLabelElement>(null);
  const debouncedCallback = useDebouncedCallback((val) => {
    onChange(id, val);
  }, 250);

  const handleOnChange = useCallback(
    (val: string) => {
      const parsedValue = parseValue ? parseValue(val) : val;
      setLocalVal(parsedValue);
      debouncedCallback(parsedValue);
    },
    [setLocalVal, debouncedCallback, parseValue],
  );

  const handleOnKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        onValueSelected?.();
      }
      onKeyDown?.(e);
    },
    [onValueSelected, onKeyDown],
  );

  // Focus the input when the component mounts (when dropdown opens)
  useEffect(() => {
    if (disableAutoFocus) return undefined;

    const timeoutId = setTimeout(() => {
      const inputElement = inputRef.current?.querySelector('input');
      if (inputElement) {
        inputElement.focus();
      }
    }, 50);
    return () => clearTimeout(timeoutId);
  }, [disableAutoFocus]);

  return (
    <FloatingField
      label={label}
      element={
        <TextInput
          {...restProps}
          ref={inputRef}
          id={id}
          onChange={handleOnChange}
          onKeyDown={handleOnKeyDown}
          value={localVal}
          size="small"
          width="100%"
        />
      }
    />
  );
}

export function TableFiltersFieldText({
  id,
  value,
  label,
  onChange,
  onKeyDown,
  onValueSelected,
}: TextFilterProps & { onValueSelected?: () => void }) {
  return (
    <CompositeField
      label={label}
      element={
        <TableFiltersFieldDebouncedInput
          id={id}
          label={label}
          onChange={onChange}
          onKeyDown={onKeyDown}
          value={value}
          onValueSelected={onValueSelected}
        />
      }
    />
  );
}

const NumberRangeFieldsIcon = styled(Icon, {
  width: '$12',
  height: '$12',
});
NumberRangeFieldsIcon.displayName = 'NumberRangeFieldsIcon';

export function TableFiltersFieldNumber({
  id,
  value,
  label,
  handleFiltersChange,
  onKeyDown,
  onValueSelected,
}: {
  id: string;
  value: number | null;
  label: string;
  handleFiltersChange: (id: string, value: number | null) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onValueSelected?: () => void;
}) {
  return (
    <CompositeField
      label={label}
      element={
        <TableFiltersFieldDebouncedInput
          id={id}
          label={label}
          onChange={(inputId, key) => {
            const num = Number(key);
            const val = key === '' ? null : num;
            handleFiltersChange(inputId, val);
          }}
          onKeyDown={onKeyDown}
          value={value === null ? '' : String(value)}
          type="number"
          onValueSelected={onValueSelected}
        />
      }
    />
  );
}

export function TableFiltersFieldNumberRange({
  id: idProp,
  value,
  label,
  handleFiltersChange,
  onKeyDown,
  onValueSelected,
}: {
  id: string;
  value: [number | null, number | null];
  label: string;
  handleFiltersChange: (id: string, value: [number | null, number | null]) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onValueSelected?: () => void;
}) {
  const isInvalidRange = isNumber(value[0]) && isNumber(value[1]) && value[0] > value[1];
  const rangeRef = useRef<HTMLDivElement>(null);

  // Auto-focus the first input (min) when the dropdown opens
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const firstInput = rangeRef.current?.querySelector('input');
      if (firstInput) {
        firstInput.focus();
      }
    }, 50);
    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <div ref={rangeRef}>
      <FloatingImmediateErrorField
        errorMessage={isInvalidRange ? 'Minimum cannot be above the maximum.' : undefined}
        label="Number range"
        element={
          <TableFiltersFieldRow>
            <CompositeField
              label="Min"
              element={
                <TableFiltersFieldDebouncedInput
                  id={`${idProp}-min`}
                  label={`${label} min.`}
                  onChange={(id, key) => {
                    const min = Number(key);
                    const max = value[1];
                    const val = key === '' ? null : min;
                    handleFiltersChange(idProp, [val, max]);
                  }}
                  onKeyDown={onKeyDown}
                  value={String(value[0])}
                  type="number"
                  onValueSelected={onValueSelected}
                />
              }
            />
            <NumberRangeFieldsIcon icon="arrow-right" />
            <CompositeField
              label="Max"
              element={
                <TableFiltersFieldDebouncedInput
                  id={`${idProp}-max`}
                  label={`${label} max.`}
                  onChange={(id, key) => {
                    const min = value[0];
                    const max = Number(key);
                    const val = key === '' ? null : max;
                    handleFiltersChange(idProp, [min, val]);
                  }}
                  onKeyDown={onKeyDown}
                  value={String(value[1])}
                  type="number"
                  onValueSelected={onValueSelected}
                  disableAutoFocus
                />
              }
            />
          </TableFiltersFieldRow>
        }
      />
    </div>
  );
}

export function TableFiltersFieldSelect({
  id,
  handleFiltersChange,
  options,
  onKeyDown,
  onValueSelected,
  value,
}: Omit<SelectFilterProps, 'label'>) {
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Auto-focus the first option when the dropdown opens
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const firstOption = dropdownRef.current?.querySelector('[role="menuitemcheckbox"]');
      if (firstOption) {
        (firstOption as HTMLElement).focus();
      }
    }, 50);
    return () => clearTimeout(timeoutId);
  }, []);

  if (!options?.length) {
    return (
      <div ref={dropdownRef}>
        <DropdownMenuItem disabled>No options found</DropdownMenuItem>
      </div>
    );
  }

  return (
    <div ref={dropdownRef}>
      {options.map((option) => (
        <DropdownMenuCheckboxItem
          key={option.value}
          checked={value === option.value}
          disabled={option.disabled}
          onKeyDown={(e) => onKeyDown?.(e as any)}
          onCheckedChange={() => {
            handleFiltersChange(id, option.value);
            onValueSelected?.();
          }}
        >
          {option.label ?? option.value}
        </DropdownMenuCheckboxItem>
      ))}
    </div>
  );
}

export function TableFiltersFieldMultiSelect({
  id,
  value,
  handleFiltersChange,
  options,
  onKeyDown,
}: Omit<MultiSelectFilterProps, 'label'>) {
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Auto-focus the first option when the dropdown opens
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const firstOption = dropdownRef.current?.querySelector('[role="menuitemcheckbox"]');
      if (firstOption) {
        (firstOption as HTMLElement).focus();
      }
    }, 50);
    return () => clearTimeout(timeoutId);
  }, []);

  if (options) {
    return (
      <div ref={dropdownRef}>
        {options.map((option) => {
          const isSelected = value.includes(option.value);
          return (
            <DropdownMenuCheckboxItem
              key={option.value}
              closeOnClick={false}
              onCheckedChange={(checked) => {
                const newValue = checked
                  ? [...value, option.value]
                  : value.filter((v) => v !== option.value);
                handleFiltersChange(id, newValue);
              }}
              onKeyDown={(e) => onKeyDown?.(e as any)}
              checked={isSelected}
              disabled={option.disabled}
            >
              {option.label ?? option.value}
            </DropdownMenuCheckboxItem>
          );
        })}
      </div>
    );
  }
  return null;
}

export function TableFiltersFieldDateRange({
  id,
  value,
  label,
  handleFiltersChange,
  onKeyDown,
  onValueSelected,
}: {
  id: string;
  value: [string | null, string | null];
  label: string;
  handleFiltersChange: (id: string, value: [string | null, string | null]) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onValueSelected?: () => void;
}) {
  const hasValue = !!value[0] && !!value[1];

  const state = useDateTimeRangePicker({
    fixAnchorToNow: false,
    allowCustom: true,
    defaultValue: hasValue ? TimePeriod.Custom : TimePeriod.OneDay,
    defaultStart: value[0] ?? undefined,
    defaultEnd: value[1] ?? undefined,
  });

  const handleChange = useEffectEvent(handleFiltersChange);

  useEffect(() => {
    handleChange(id, [state.startDate, state.endDate]);
  }, [id, state.startDate, state.endDate]);

  return (
    <FloatingImmediateErrorField
      label={label}
      element={
        <DateTimeRangePickerDropdownContents
          state={state}
          onValueChange={onValueSelected}
          onKeyDown={onKeyDown}
          options={[
            TimePeriod.ThirtyMinutes,
            TimePeriod.OneHour,
            TimePeriod.TwoHours,
            TimePeriod.ThreeHours,
            TimePeriod.SixHours,
            TimePeriod.TwelveHours,
            TimePeriod.OneDay,
            TimePeriod.SevenDays,
            TimePeriod.ThirtyDays,
          ]}
        />
      }
    />
  );
}
